<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import NavBadge from './NavBadge.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    icon: IconDefinition;
    to?: string;
    active?: boolean;
    collapsed?: boolean;
    itemHeight?: string;
    /** Optional count rendered as a NavBadge to the right of the label
     * in expanded mode, or as a small corner dot on the icon in collapsed
     * mode. Hidden when count is 0 (no zero-badges). */
    badgeCount?: number | null;
    badgeVariant?: 'primary' | 'pending' | 'info';
    /** Forces the badge / corner dot to red regardless of variant. Used
     * to flag urgency such as past-due invoices. */
    badgeDot?: boolean;
    badgeTooltip?: string;
  }>(),
  {
    active: false,
    collapsed: false,
    itemHeight: 'h-11',
    badgeCount: 0,
    badgeVariant: 'primary',
    badgeDot: false,
    badgeTooltip: undefined,
  },
);

const emit = defineEmits<{ click: [] }>();

const activeClasses = computed(() =>
  props.active
    ? [
        'border-l-primary',
        'text-primary font-semibold',
        'bg-[color-mix(in_srgb,var(--color-primary)_6%,transparent)]',
      ]
    : [],
);

// Show the collapsed corner-dot only when there's a count to surface.
const showCollapsedDot = computed(
  () =>
    props.collapsed &&
    typeof props.badgeCount === 'number' &&
    props.badgeCount > 0,
);

// Dot color: red overrides everything when badgeDot=true (urgent / past-due),
// otherwise follows the variant so the collapsed signal stays consistent
// with the expanded badge color.
const collapsedDotClass = computed(() => {
  if (props.badgeDot) return 'bg-red-500';
  switch (props.badgeVariant) {
    case 'pending':
      return 'bg-amber-500';
    case 'info':
      return 'bg-muted';
    case 'primary':
    default:
      return 'bg-primary';
  }
});
</script>

<template>
  <component
    :is="to ? RouterLink : 'button'"
    v-bind="
      to
        ? { to, 'aria-current': active ? 'page' : undefined }
        : { type: 'button' }
    "
    class="flex items-center w-full whitespace-nowrap overflow-hidden text-sm font-medium border-l-[3px] border-transparent text-foreground transition-[background,color,border-color] duration-120 hover:bg-canvas"
    :class="[itemHeight, ...activeClasses]"
    @click="emit('click')"
  >
    <span class="relative shrink-0 w-[53px] flex items-center justify-center">
      <FontAwesomeIcon
        :icon="icon"
        class="w-[18px] text-[0.9375rem]"
        :class="active ? 'text-primary' : 'text-muted'"
      />
      <!-- Collapsed-sidebar attention dot: small colored circle in the
           icon's top-right corner. Hidden in expanded mode (the full
           NavBadge below takes over). -->
      <span
        v-if="showCollapsedDot"
        class="absolute top-1/4 right-3 size-2 rounded-full border border-canvas"
        :class="collapsedDotClass"
        :title="badgeTooltip"
        :aria-label="badgeTooltip ?? `${badgeCount}`"
        data-testid="nav-item-collapsed-dot"
      />
    </span>
    <!-- Label + right-aligned badge (expanded mode only). flex-1 absorbs
         the slack between the label and the badge so the badge anchors
         to the right edge regardless of label length. -->
    <span
      class="flex flex-1 items-center gap-2 overflow-hidden whitespace-nowrap pr-4 transition-[max-width,opacity] duration-200"
      :class="collapsed ? 'max-w-0 opacity-0' : 'max-w-full opacity-100'"
    >
      <span class="truncate">{{ label }}</span>
      <NavBadge
        v-if="!collapsed"
        :count="badgeCount"
        :variant="badgeVariant"
        :dot="badgeDot"
        :tooltip="badgeTooltip"
        :active="active"
        class="ml-auto"
      />
    </span>
  </component>
</template>
